import type { AssignmentTemplate, AssignmentTemplatePayload } from '../../model/assignments/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssignmentTemplateUpdateApiOptions {
  templateId: string | number
  /** A true `PATCH`: absent keys are never written (unlike the assessment sibling). Explicit `null`
   *  clears `curriculum_topic_id`, `curriculum_lesson_id` and `cover_path`; send `cover_path`,
   *  never `cover_url`. A lesson must belong to the row's topic, so always send the pair. */
  payload: Partial<AssignmentTemplatePayload>
  onStart?: () => void
  onSuccess?: (template: AssignmentTemplate) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `PATCH /admin-api/assignment-templates/{template_id}`. The server validates neither the document
 *  nor the status transition — the publish gate is app-admin's alone. */
export const useAssignmentTemplateUpdateApi = async (
  options: AssignmentTemplateUpdateApiOptions,
): Promise<AssignmentTemplate | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assignment-templates.update', { template_id: options.templateId })

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.patch<ApiResponse>(url, options.payload)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const template: AssignmentTemplate | null = data.data?.template ?? null

    if (!template) {
      const msg = 'Unexpected error. The assignment template is missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    toast.success('Assignment template updated')
    options.onSuccess?.(template)
    return template
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
